import classNames from "classnames";
import React, {
  ChangeEvent,
  CSSProperties,
  FunctionComponent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import "./style.scss";

interface Props {
  className?: string;
  focus?: boolean;
  maxRows?: number;
  onChange: (newText: string) => void;
  placeholder: string;
  style?: CSSProperties;
  value: string;
}

const AdvancedTextArea: FunctionComponent<Props> = ({
  className,
  focus,
  maxRows,
  onChange,
  placeholder,
  style = {},
  value,
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const shadowTextareaRef = useRef<HTMLDivElement>(null);
  const [selection, setSelection] = useState<{ end: number; start: number }>({
    end: value.length,
    start: value.length,
  });
  const [changedLength, setChangedLength] = useState<number>(0);
  const [textCanceled, setTextCanceled] = useState<boolean>(false);
  const [rows, setRows] = useState<number>(1);

  useEffect(() => {
    const element = textareaRef.current;
    if (!element) {
      return;
    }

    if (textCanceled) {
      element.selectionStart = selection.start - changedLength;
      element.selectionEnd = selection.end - changedLength;
      setTextCanceled(false);
    } else if (changedLength < 0) {
      element.selectionStart = selection.start;
      element.selectionEnd = selection.end;
    }
    if (element && focus) {
      element.focus();
    }
  }, [focus, changedLength, selection, textCanceled]);

  useEffect(() => {
    const element = textareaRef.current;
    if (!element) {
      return;
    }

    if (element && focus) {
      element.focus();
    }
  }, [className, focus]);

  const handleFocus = useCallback(() => {
    const element = textareaRef.current;
    if (element) {
      element.selectionStart = selection.start;
      element.selectionEnd = selection.end;
    }
  }, [selection]);

  const setSelectionFromEvent = (event: ChangeEvent<HTMLTextAreaElement>) => {
    setSelection({
      end: event.target.selectionEnd,
      start: event.target.selectionStart,
    });
  };

  const getRows = (text: string) => {
    if (!textareaRef.current) {
      return 1;
    }
    const lineSize =
      style && style.lineHeight
        ? style.lineHeight.toString().replace("px", "")
        : textareaRef.current.style.lineHeight.replace("px", "");
    let textContentHeight = textareaRef.current.scrollHeight;

    if (shadowTextareaRef.current && lineSize) {
      shadowTextareaRef.current.innerHTML = text
        .replace(/(?:\r\n|\n)/g, "<br/>")
        .replace(/<br\/>$/, "<br/> "); // second replace is used to render a new line in comparative div
      textContentHeight = shadowTextareaRef.current.offsetHeight;
    }

    return textContentHeight && lineSize ? Math.round(textContentHeight / +lineSize) : 1;
  };
  const getLimitedRows = (text: string) => {
    const nbRows = getRows(text);
    return !maxRows || maxRows > nbRows ? nbRows : maxRows;
  };

  const limitValue = (text: string) => {
    if (!maxRows) {
      return text;
    }

    let newText = text;
    const totalRows = getRows(text);
    const rowsDiff = totalRows - maxRows;

    if (rowsDiff > 1) {
      const rowsRatio = (rowsDiff - 1) / totalRows;
      const charsToDelete = Math.round(text.length * rowsRatio);

      newText = newText.substr(0, newText.length - charsToDelete);
    }

    let nbRows = getRows(newText);
    while (nbRows > maxRows) {
      newText = newText.substr(0, newText.length - 1);
      nbRows = getRows(newText);
    }

    return newText;
  };

  const handleChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    const newText = event.target.value.replace(/  /, " ");
    setSelectionFromEvent(event);
    const totalRows = getRows(newText);

    if (rows === maxRows && newText.search(/\n$/) > 0 && newText.length - value.length === 1) {
      updateFieldAndChange(value);
    } else if (maxRows && totalRows > maxRows && newText.length > value.length) {
      if (newText.length - value.length > 1) {
        const limitedValue = limitValue(newText);
        updateFieldAndChange(limitedValue);
      } else {
        updateFieldAndChange(value);
      }
      setTextCanceled(true);
    } else {
      updateFieldAndChange(newText);
    }
  };

  useEffect(() => {
    const rowsNumber = getLimitedRows(value);
    if (textareaRef.current && rows !== rowsNumber) {
      setRows(rowsNumber);
    }
  }, [textareaRef, value]);

  useEffect(() => {
    if (!maxRows) {
      return;
    }
    const rowsNumber = getRows(value);
    if (textareaRef.current && rowsNumber > maxRows) {
      const limitedValue = limitValue(value);
      onChange(limitedValue);
    }
  }, [style, value]);

  const updateFieldAndChange = (text: string) => {
    setChangedLength(text.length - value.length);
    setRows(getLimitedRows(text));
    onChange(text);
  };

  return (
    <>
      <div
        className={classNames("AdvancedTextArea--shadow", className)}
        ref={shadowTextareaRef}
        style={style}
        dangerouslySetInnerHTML={{ __html: value.replace(/(?:\r\n|\r|\n)/g, "<br/>") }}
      />
      <textarea
        ref={textareaRef}
        className={className}
        onChange={handleChange}
        onFocus={handleFocus}
        placeholder={placeholder}
        rows={rows}
        style={style}
        value={value}
      />
    </>
  );
};

export default AdvancedTextArea;
